import React from 'react';
import { Loader2, Lightbulb, Sparkles, Code } from 'lucide-react';
import { ResultMessage } from './ResultMessage';
import { CodeOnlyMessage } from './CodeOnlyMessage';

const EMPTY_STATE_CARDS = [
  { icon: Lightbulb, title: 'Describe Your Idea', desc: 'Tell me what app you want to build' },
  { icon: Sparkles, title: 'AI Enhancement', desc: 'Get features, tech stack & MVP suggestions' },
  { icon: Code, title: 'Get Code', desc: 'Receive scaffold & starter code instantly' },
];

const EXAMPLE_PROMPTS = [
  'A task management app with team collaboration',
  'An AI-powered recipe generator',
  'A fitness tracking app',
];

// Renders the empty state or the full conversation thread
export const ConversationView = ({
  conversation,
  messagesEndRef,
  expandedSections,
  toggleSection,
  copiedItems,
  copyToClipboard,
  onFollowUp,
  isProcessing,
  onExampleClick,
}) => {
  if (conversation.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl">
          {EMPTY_STATE_CARDS.map((item) => (
            <div key={item.title} className="bg-[#302f2c] border border-[#1e3a5f]/50 rounded-xl p-4 text-center">
              <item.icon className="w-7 h-7 text-[#7dd3fc] mx-auto mb-2" />
              <h3 className="text-[#7dd3fc] font-semibold text-sm mb-1">{item.title}</h3>
              <p className="text-[#888680] text-xs">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* Example prompts */}
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          {EXAMPLE_PROMPTS.map((example, idx) => (
            <button
              key={`example-${idx}`}
              onClick={() => onExampleClick(example)}
              data-testid={`example-prompt-${idx}`}
              className="px-3 py-1.5 bg-[#302f2c] border border-[#1e3a5f]/50 rounded-full text-[#888680] text-xs hover:border-[#7dd3fc]/50 hover:text-[#7dd3fc] transition-all"
            >
              {example}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      {conversation.map((message, idx) => (
        <div key={message.id || `msg-${idx}`} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
          {message.type === 'user' ? (
            <div className="max-w-[80%] bg-[#7dd3fc] text-[#1a1c1b] rounded-2xl rounded-tr-sm px-4 py-2.5">
              <p className="font-medium text-sm">{message.content}</p>
            </div>
          ) : message.content === 'thinking' ? (
            <div className="max-w-[80%] bg-[#302f2c] border border-[#1e3a5f]/50 rounded-2xl rounded-tl-sm px-4 py-3">
              <div className="flex items-center gap-3">
                <Loader2 className="w-4 h-4 text-[#7dd3fc] animate-spin" />
                <span className="text-[#888680] text-sm">{message.text}</span>
              </div>
            </div>
          ) : message.content === 'error' ? (
            <div className="max-w-[80%] bg-red-500/10 border border-red-500/30 rounded-2xl rounded-tl-sm px-4 py-3">
              <p className="text-red-400 text-sm">{message.text}</p>
            </div>
          ) : message.content === 'result' ? (
            <ResultMessage
              message={message}
              expandedSections={expandedSections}
              toggleSection={toggleSection}
              copiedItems={copiedItems}
              copyToClipboard={copyToClipboard}
              onFollowUp={onFollowUp}
              isProcessing={isProcessing}
            />
          ) : message.content === 'code-only' ? (
            <CodeOnlyMessage
              message={message}
              copiedItems={copiedItems}
              copyToClipboard={copyToClipboard}
            />
          ) : null}
        </div>
      ))}
      <div ref={messagesEndRef} />
    </>
  );
};
